import { hasActiveCapture } from "./buildings";
import { computeDamageFromRoll } from "./damage";
import { resetCaptureAt } from "./economy";
import { horizontalFacingToward } from "./fixtures";
import { findUnit, getTile, manhattan } from "./grid";
import { applyDamageToStacks } from "./stacks";
import { isArmedLandTransport } from "./transport";
import type { GameEvent, GameState, Unit } from "./types";
import { canUnitDamage, effectiveAttackKind, isInAttackRange, isIndirectFire } from "./units";
import { countOwnedMonuments } from "./victory";

function setUnit(state: GameState, { id, patch }: { id: string; patch: Partial<Unit> }): GameState {
  return {
    ...state,
    units: state.units.map((u) => (u.id === id ? { ...u, ...patch } : u)),
  };
}

export function removeUnitWithCaptureReset({
  state,
  id,
  events,
}: {
  state: GameState;
  id: string;
  events: GameEvent[];
}): GameState {
  const unit = state.units.find((u) => u.id === id);
  if (unit) {
    const tile = getTile(state, unit.pos);
    if (hasActiveCapture(tile)) {
      const { state: afterReset, events: resetEvents } = resetCaptureAt({
        state,
        pos: unit.pos,
        reason: "killed",
      });
      events.push(...resetEvents);
      return { ...afterReset, units: afterReset.units.filter((u) => u.id !== id) };
    }
  }
  return { ...state, units: state.units.filter((u) => u.id !== id) };
}

function canCounterAttack({ attacker, defender }: { attacker: Unit; defender: Unit }): boolean {
  if (!isInAttackRange(defender.kind, manhattan(defender.pos, attacker.pos))) return false;
  if (
    isIndirectFire(effectiveAttackKind(attacker)) ||
    isIndirectFire(effectiveAttackKind(defender))
  ) {
    return false;
  }
  return canUnitDamage({ unit: defender, defenderKind: effectiveAttackKind(attacker) });
}

function killCreditPatch(unit: Unit): Partial<Unit> {
  if (isArmedLandTransport(unit) && unit.cargo) {
    return {
      cargo: {
        ...unit.cargo,
        kills: (unit.cargo.kills ?? 0) + 1,
      },
    };
  }
  return { kills: (unit.kills ?? 0) + 1 };
}

function creditKill(state: GameState, unit: Unit): GameState {
  return setUnit(state, { id: unit.id, patch: killCreditPatch(unit) });
}

export function applyCounterAttackIfEligible({
  next,
  attackerId,
  targetId,
  events,
}: {
  next: GameState;
  attackerId: string;
  targetId: string;
  events: GameEvent[];
}): GameState {
  const updatedTarget = findUnit(next, targetId);
  const updatedAttacker = findUnit(next, attackerId);
  if (!canCounterAttack({ attacker: updatedAttacker, defender: updatedTarget })) return next;
  const counterDmg = computeDamageFromRoll({
    attacker: updatedTarget,
    defender: updatedAttacker,
    defenderTile: getTile(next, updatedAttacker.pos),
    attackerMonumentCount: countOwnedMonuments(next.tiles, updatedTarget.owner),
  });
  const counterApplied = applyDamageToStacks({ unit: updatedAttacker, damage: counterDmg });
  const counterKilled = counterApplied.killed;
  let after = counterKilled
    ? removeUnitWithCaptureReset({ state: next, id: attackerId, events })
    : setUnit(next, { id: attackerId, patch: { hp: counterApplied.hp } });
  if (counterKilled) after = creditKill(after, updatedTarget);
  events.push({
    type: "countered",
    playerId: updatedTarget.owner,
    attackerKind: updatedAttacker.kind,
    counterKind: effectiveAttackKind(updatedTarget),
    attackerId: targetId,
    targetId: attackerId,
    damage: counterDmg,
    killed: counterKilled,
  });
  return after;
}

export function applyPrimaryAttackHit({
  state,
  attacker,
  target,
  damage,
  events,
}: {
  state: GameState;
  attacker: Unit;
  target: Unit;
  damage: number;
  events: GameEvent[];
}): { next: GameState; killed: boolean } {
  const applied = applyDamageToStacks({ unit: target, damage });
  if (applied.killed) {
    return {
      next: removeUnitWithCaptureReset({ state, id: target.id, events }),
      killed: true,
    };
  }
  return {
    next: setUnit(state, {
      id: target.id,
      patch: {
        hp: applied.hp,
        facing: horizontalFacingToward({
          from: target.pos,
          to: attacker.pos,
          fallback: target.facing,
        }),
      },
    }),
    killed: false,
  };
}

export function markAttackerResolved({
  state,
  attacker,
  killed,
}: {
  state: GameState;
  attacker: Unit;
  killed: boolean;
}): GameState {
  return setUnit(state, {
    id: attacker.id,
    patch: {
      hasMoved: true,
      hasAttacked: true,
      ...(killed ? killCreditPatch(attacker) : {}),
    },
  });
}
